import React, {useEffect, useMemo, useState} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import {
	ButtonDropdown,
	DropdownList,
	FlexColumn,
	FlexRow,
	Icon,
	Label,
	Modal,
	SearchField,
	SubHeading,
	Table,
	Text,
} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {AddBillToEntityModal_viewer} from './__generated__/AddBillToEntityModal_viewer.graphql';
import Util from '../../../shared/util/util';
import InlineLoader from '../../../shared/components/inline-loader/inline_loader';
import CreateBulkBillToMutation from '../../../../mutations/invoicing/CreateBulkBillToMutation';
import {
	Contact,
	fetchEconomicContacts,
	fetchQuickbooksContacts,
	fetchXeroContacts,
	filterContactsByIntegrationInstanceId,
} from '../ts/InvoiceIntegrationsUtils';
import InvoicingIntegrationIcon from '../ts/InvocingIntegrationIcon';

type AddBillToEntityModalProps = {
	billFromId: string;
	closeModal: () => void;
	viewer: AddBillToEntityModal_viewer;
};

const AddBillToEntityModal = ({billFromId, closeModal, viewer}: AddBillToEntityModalProps) => {
	const {formatMessage} = useIntl();
	const [searchValue, setSearchValue] = useState('');
	const [isLoading, setIsLoading] = useState(true);
	const [contacts, setContacts] = useState<Contact[]>([]);
	const [initialContactsList, setInitialContactsList] = useState<Contact[]>([]);
	const billFroms = viewer.company?.billFroms || [];
	const [billFrom, setBillFrom] = useState(billFroms.find(billFrom => billFrom?.id === billFromId));
	const alreadyImported = useMemo(() => new Set(billFrom?.billTos.edges?.map(edge => edge?.node?.externalId)), [billFrom]);
	const [selectedRows, setSelectedRows] = useState<Contact[]>([]);
	const [filteredRows, setFilteredRows] = useState<Contact[]>([]);
	const filteredContactsCount = contacts.length - filteredRows.length;
	const [xeroFetched, setXeroFetched] = useState(false);
	const [economicFetched, setEconomicFetched] = useState(false);
	const [quickbooksFetched, setQuickbooksFetched] = useState(false);

	useEffect(() => {
		setIsLoading(true);

		const onXeroSuccess = (newContacts: Contact[]) => {
			setInitialContactsList(prevValue => [...prevValue, ...newContacts]);
			setXeroFetched(true);
		};

		const onQuickbooksSuccess = (newContacts: Contact[]) => {
			setInitialContactsList(prevValue => [...prevValue, ...newContacts]);
			setEconomicFetched(true);
		};

		const onEconomicSuccess = (newContacts: Contact[]) => {
			setInitialContactsList(prevValue => [...prevValue, ...newContacts]);
			setQuickbooksFetched(true);
		};

		fetchXeroContacts().then(onXeroSuccess);
		fetchQuickbooksContacts().then(onQuickbooksSuccess);
		fetchEconomicContacts().then(onEconomicSuccess);
	}, []);

	useEffect(() => {
		if (xeroFetched && economicFetched && quickbooksFetched) {
			setIsLoading(false);
			const filteredContacts = filterContactsByIntegrationInstanceId(initialContactsList, billFrom);
			setContacts(filteredContacts);
			setFilteredRows(filteredContacts);
		}
	}, [initialContactsList, billFrom, xeroFetched, economicFetched, quickbooksFetched]);

	const importBillTos = () => {
		if (selectedRows.length) {
			Util.CommitMutation(
				CreateBulkBillToMutation,
				{
					billTos: selectedRows.map(row => ({
						name: row.name,
						address: row.address,
						taxId: row.taxId,
						externalId: row.id,
						billFromId: billFrom?.id,
					})),
				},
				closeModal
			);
		} else {
			closeModal();
		}
	};

	const calculateNoOfCustomers = billFrom => {
		return isLoading
			? ''
			: `(${
					initialContactsList.filter(
						contact =>
							(!contact.integrationInstanceId || contact.integrationInstanceId === billFrom?.externalId) &&
							contact.integration === billFrom?.integration
					).length
			  })`;
	};

	const BillFromItem = ({billFrom}) => (
		<FlexRow gap={'s'}>
			<InvoicingIntegrationIcon integration={billFrom?.integration} />
			<div>
				{billFrom?.name} {calculateNoOfCustomers(billFrom)}
			</div>
		</FlexRow>
	);

	const alreadyImportedIcon = {
		label: formatMessage({id: 'settings_finance.bill_to.already_added'}),
		icon: 'checkCircle',
		color: 'success',
	};
	return (
		<Modal onCloseModal={closeModal}>
			<Modal.Title>
				<FormattedMessage id="settings_finance.add_bill_to_entities" />
			</Modal.Title>
			<Modal.Content>
				<SearchField
					isExpandable={false}
					value={searchValue}
					onChange={setSearchValue}
					placeholder={formatMessage({id: 'common.search'})}
				/>

				{billFroms.length === 1 ? (
					<Label>
						<BillFromItem billFrom={billFrom} />
					</Label>
				) : (
					<DropdownList>
						<DropdownList.Trigger>
							<ButtonDropdown emphasis={'medium'} style={{width: 'fit-content'}}>
								<BillFromItem billFrom={billFrom} />
							</ButtonDropdown>
						</DropdownList.Trigger>
						<DropdownList.Content offset={5} align={'start'}>
							{billFroms.map(current => (
								<DropdownList.Item key={current?.id} onClick={() => setBillFrom(current)}>
									<FlexRow>
										<BillFromItem billFrom={current} />
										{current?.id === billFrom?.id && <Icon icon={'iconTick'} />}
									</FlexRow>
								</DropdownList.Item>
							))}
						</DropdownList.Content>
					</DropdownList>
				)}

				{isLoading ? (
					<InlineLoader />
				) : (
					<>
						<div>
							<Table
								data={contacts}
								searchValue={searchValue}
								onSelectionChange={setSelectedRows}
								onFilteringChange={setFilteredRows}
								enableRowSelection={row => !alreadyImported.has(row.original.id)}
								width={'100%'}
							>
								<Table.TextColumn header={formatMessage({id: 'common.select_all'})} accessorKey={'name'} />
								<Table.IconColumn
									accessorKey={'id'}
									header={'Status'}
									hideHeader
									align={Table.Align.right}
									icon={(value: string) => (alreadyImported.has(value) ? alreadyImportedIcon : undefined)}
								/>
							</Table>
						</div>
						<FlexColumn alignItems={'center'}>
							{filteredRows.length === 0 && (
								<SubHeading size={'2'}>
									<FormattedMessage id={'settings_finance.bill_to.no_results'} />
								</SubHeading>
							)}
							{filteredContactsCount > 0 && (
								<Text>
									<FormattedMessage
										id={'settings_finance.bill_to.filtering_count'}
										values={{count: filteredContactsCount}}
									/>
								</Text>
							)}
						</FlexColumn>
					</>
				)}
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={importBillTos} disabled={!selectedRows.length}>
					<FormattedMessage id={'settings_finance.bill_to.add_selected'} values={{count: selectedRows.length}} />
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={closeModal}>
					<FormattedMessage id={'common.close'} />
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export const AddBillToEntityModalQuery = graphql`
	query AddBillToEntityModalQuery {
		viewer {
			actualPersonId
			component(name: "AddBillToEntityModal")
			...AddBillToEntityModal_viewer
		}
	}
`;

export default createFragmentContainer(AddBillToEntityModal, {
	viewer: graphql`
		fragment AddBillToEntityModal_viewer on Viewer {
			id
			company {
				id
				billFroms {
					id
					integration
					externalId
					name
					billTos(first: 10000) @connection(key: "BillFrom_billTos", filters: []) {
						edges {
							node {
								id
								name
								address
								taxId
								externalId
							}
						}
					}
				}
			}
		}
	`,
});
